import type { TableProps } from '@workramp/ui';
import type { CMSAssetItem, CMSAssetRecentActivity, CMSRecentActivityTableColumn } from '../../types';
import { Table } from '@workramp/ui';
import { useState } from 'react';
import { useTheme } from 'styled-components';
import { ErrorPage, UserMediaObject } from '~/features/common';
import { I18n } from '~/common/explicit-exports';
import { USER_PROFILE_ROUTE } from '~/elc/features/admin-content-overview/constants';
import { DateTimeCell } from '~/features/tables';
import { useGetRecentActivity } from '../../api';
import { CMSShareLinkAuditAction, CMSAssetAuditAction } from '../../constants';
import { CMSEmptyTable } from '../CMSEmptyTable';

const { t } = I18n;
const DEFAULT_ROWS_PER_PAGE = 50;
const DEFAULT_CURRENT_PAGE = 1;
const DEFAULT_PAGINATION_ITEMS_PER_PAGE = 25;
const DEFAULT_PAGINATION_ITEMS_PER_PAGE_OPTIONS = [25, 50, 100];

export const columns: CMSRecentActivityTableColumn[] = [
  { name: t('js.admin.cms.recent_activity_table.columns.name'), key: 'name', allowSorting: false },
  { name: t('js.admin.cms.recent_activity_table.columns.activity'), key: 'activity', allowSorting: false },
  { name: t('js.admin.cms.recent_activity_table.columns.date'), key: 'date', allowSorting: false },
];

export interface CMSRecentActivityTableProps {
  asset: CMSAssetItem;
}

const getActionString = (activity: CMSAssetAuditAction | CMSShareLinkAuditAction, assetType: string): string => {
  if (activity === CMSAssetAuditAction.DOWNLOAD && assetType === 'LINK') {
    return t('js.admin.cms.recent_activity_status.link_copy');
  }
  if (activity === CMSAssetAuditAction.DOWNLOAD || activity === CMSShareLinkAuditAction.DOWNLOAD) {
    return t('js.admin.cms.recent_activity_status.file_download');
  }

  if (activity === CMSAssetAuditAction.VIEW || activity === CMSShareLinkAuditAction.VIEW) {
    return t('js.admin.cms.recent_activity_status.view');
  }
  if (activity === CMSAssetAuditAction.EXTERNAL_SHARE || activity === CMSShareLinkAuditAction.SHARE) {
    return t('js.admin.cms.recent_activity_status.share');
  }

  return t('js.admin.cms.recent_activity_status.unknown');
};

export const CMSRecentActivityTable = ({ asset }: CMSRecentActivityTableProps) => {
  const [currentPage, setCurrentPage] = useState(DEFAULT_CURRENT_PAGE);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const getRecentActivityAPI = useGetRecentActivity(asset.id, currentPage, rowsPerPage);
  const theme = useTheme();

  if (getRecentActivityAPI.isError) {
    return <ErrorPage status={getRecentActivityAPI?.error?.status} />;
  }

  let tableProps: TableProps<never> = {
    emptyState: (
      <CMSEmptyTable
        asset={asset}
        subheading={t('js.admin.cms.recent_activity_table.empty_title')}
        body={t('js.admin.cms.recent_activity_table.empty_description')}
      />
    ),
    isLoading: getRecentActivityAPI.isLoading,
    showLastRowDivider: true,
    rowPadding: theme.spacing(8),
  };

  if (getRecentActivityAPI.data?.totalCount > DEFAULT_PAGINATION_ITEMS_PER_PAGE) {
    tableProps = {
      ...tableProps,
      page: currentPage,
      allowsItemsPerPage: true,
      itemsPerPage: rowsPerPage,
      itemsPerPageOptions: DEFAULT_PAGINATION_ITEMS_PER_PAGE_OPTIONS,
      onPageChange: setCurrentPage,
      onItemsPerPageChange: setRowsPerPage,
      totalItems: getRecentActivityAPI.data?.totalCount,
    };
  }

  return (
    <Table {...tableProps}>
      <Table.Header>
        {columns.map(({ name, key }) => (
          <Table.Column key={key}>{name}</Table.Column>
        ))}
      </Table.Header>
      <Table.Body>
        {getRecentActivityAPI.data?.data?.map((row: CMSAssetRecentActivity) => {
          const { id, userName, userEmail, activity, createdAt, userId } = row;
          const href = userId ? `${USER_PROFILE_ROUTE}/${userId}` : undefined;

          return (
            <Table.Row key={`${id}-${createdAt}`}>
              <Table.Cell>
                <UserMediaObject name={userName} email={userEmail} href={href} />
              </Table.Cell>
              <Table.Cell>{getActionString(activity, asset.assetType)} </Table.Cell>
              <Table.Cell>
                <DateTimeCell isLocalTime date={new Date(createdAt).valueOf()} />
              </Table.Cell>
            </Table.Row>
          );
        })}
      </Table.Body>
    </Table>
  );
};
